import React from "react";
import moment from "moment";
import { cn } from "../../../../utils/classnames";

// ─── Format helpers ────────────────────────────────────────────────────────────

const formatDateTime = (value, withWeekday = true) => {
  if (!value) return "-";
  const m = moment(value);
  return withWeekday ? m.format("MMM. Do, YYYY (ddd) H:mm") : m.format("MMM. Do, YYYY H:mm");
};

const formatJPY = (value) => {
  const n = Number(value);
  if (!n && n !== 0) return "-";
  if (n === 0) return "0 JPY";
  return `${n.toLocaleString()} JPY`;
};

const formatDurationText = (duration) => {
  if (!duration) return "-";
  const [h = "0", m = "0"] = String(duration).split(":");
  const rounded = Math.ceil(((Number(h) * 60 + Number(m)) / 60) * 2) / 2;
  return `${rounded} Hours`;
};

const formatBilledHours = (minutes) => {
  if (minutes === null || minutes === undefined) return "-";
  return `${Math.ceil((Number(minutes) / 60) * 2) / 2} Hours`;
};

const formatPaxGroup = (paxList = []) => {
  if (!Array.isArray(paxList)) return "-";
  const text = paxList.filter(i => Number(i?.paxcount || 0) > 0).map(i => `${i?.paxtype || "-"}: ${i?.paxcount}`).join(", ");
  return text || "-";
};

const formatLuggageGroup = (list = []) => {
  if (!Array.isArray(list)) return "-";
  const text = list.filter(i => Number(i?.luggagecount || 0) > 0).map(i => {
    const label = String(i?.luggagetype || "").toLowerCase().includes("check") ? "Check-in" : "Cabin";
    return `${label}: ${i?.luggagecount}`;
  }).join(", ");
  return text || "-";
};

const MEET_ASSIST_LABELS = {
  meetAssist: "Meet & Assist",
  vipMeetAssist: "VIP Meet & Assist",
};

const formatMeetAssist = (meetAssistList = []) => {
  const arrival = meetAssistList.find((item) => item?.meetAssistType === "arrival")?.meetAssistName;
  const departure = meetAssistList.find((item) => item?.meetAssistType === "departure")?.meetAssistName;

  const hasArrival = Boolean(arrival && arrival !== "none");
  const hasDeparture = Boolean(departure && departure !== "none");

  if (!hasArrival && !hasDeparture) {
    return { title: "None" };
  }

  let text = "";
  if (hasArrival) {
    text += `${MEET_ASSIST_LABELS[arrival] || arrival} (Arrival)`;
  }
  if (hasDeparture) {
    if (text) text += "\n";
    text += `${MEET_ASSIST_LABELS[departure] || departure} (Departure)`;
  }

  return { title: text };
};

const formatTransferType = (type, isAirportTransfer) => {
  if (type === "byTheHour") return "Charter / By The Hour";
  if (isAirportTransfer) return "Airport Transfer";
  return "Specified Destination";
};

const formatTravelArea = (value) => {
  if (!value) return "-";
  if (value.includes("neighboringPerfecture")) return "To the neighboring city/prefecture";
  if (value.includes("withinCity")) return "Within city";
  if (value.includes("undecided")) return "Undecided";
  return value;
};

const getLocationDetail = (loc) => {
  if (!loc?.formatted_address && !loc?.label) return { name: "-", address: "" };
  if (loc?.formatted_address) {
    const parts = loc.formatted_address.split(",");
    return { name: parts[0] || "-", address: parts.slice(1).join(",").trim() };
  }
  return { name: loc?.label || "-", address: "" };
};

const formatMessage = (message) => {
  if (!message) return "-";
  if (Array.isArray(message)) return message?.[0]?.note || "-";
  return String(message).trim() || "-";
};

const getPickupSign = (honorific, name) => {
  if (!name) return "-";
  return `${honorific || ""} ${name}`.trim();
};

const getOldBookingPaymentInfo = (booking, deferredPayment) => {
  if (deferredPayment) {
    if (booking?.paymentStatus !== "CHARGE_PENDING") {
      if (booking?.confirmWithEditedFee) return "Added to monthly invoice with edited fee";
      return "Added to monthly invoice";
    }
  } else {
    if (booking?.paymentStatus !== "CHARGE_PENDING") {
      if (booking?.editedFeeType === "CHARGE_EDITED_FEE") return "Charged with edited fee";
      return "Charged";
    }
  }
  return "Not charged";
};

// ─── Diff helpers ──────────────────────────────────────────────────────────────

const stripMeta = (value) => {
  if (Array.isArray(value)) return value.map(stripMeta);
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach(k => {
      if (["_id", "__v", "createdAt", "updatedAt", "date"].includes(k)) return;
      next[k] = stripMeta(value[k]);
    });
    return next;
  }
  return value;
};

const normalizeLocation = (loc) => {
  if (!loc) return null;
  return { value: loc?.value || "", formatted_address: loc?.formatted_address || "", label: loc?.label || "" };
};

const normalizeVehicleId = (v) => {
  if (!v) return null;
  const id = typeof v?.vehicleTypeID === "object" ? v?.vehicleTypeID?._id || "" : v?.vehicleTypeID || "";
  return { vehicleTypeName: v?.vehicleTypeName || v?.vehicleTypeID?.typename || "", vehicleTypeID: id };
};

const deepEqual = (a, b) => {
  try { return JSON.stringify(stripMeta(a)) === JSON.stringify(stripMeta(b)); } catch { return a === b; }
};

const isChangedByKey = (key, oldVal, newVal) => {
  if (key === "pickupLocation") return !deepEqual(normalizeLocation(oldVal), normalizeLocation(newVal));
  if (key === "destinations") {
    const oN = Array.isArray(oldVal) ? oldVal.map(normalizeLocation) : [];
    const nN = Array.isArray(newVal) ? newVal.map(normalizeLocation) : [];
    return !deepEqual(oN, nN);
  }
  if (key === "bookingInfo") {
    const oN = Array.isArray(oldVal) ? oldVal.map(normalizeVehicleId) : [];
    const nN = Array.isArray(newVal) ? newVal.map(normalizeVehicleId) : [];
    return !deepEqual(oN, nN);
  }
  return !deepEqual(oldVal, newVal);
};

// ─── Primitive UI cells ────────────────────────────────────────────────────────

const SectionRow = ({ title }) => (
  <div className="tw:min-h-[36px] tw:py-2 tw:w-full tw:border-x tw:border-b tw:border-[#e8e8e8] tw:bg-[#f9f9f9] tw:px-5 tw:flex tw:items-center tw:justify-center">
    <span className="tw:text-[#8d8d8d] tw:text-[14px] tw:leading-[20px] tw:font-hanken-grotesk tw:text-center">{title}</span>
  </div>
);

const LabelCell = ({ label, isFirst = false }) => (
  <div className={cn(
    "tw:w-[90px] tw:sm:w-[225px] tw:px-2 tw:sm:px-5 tw:py-[14px] tw:border-l tw:border-b tw:border-[#e8e8e8] tw:bg-white tw:flex tw:items-center",
    isFirst && "tw:rounded-bl-[5px]"
  )}>
    <span className="tw:text-[#8d8d8d] tw:text-[11px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:font-hanken-grotesk">{label}</span>
  </div>
);

const ValueCell = ({ children, changed = false, muted = false, right = false, isLast = false }) => (
  <div className={cn(
    "tw:flex-1 tw:min-h-[68px] tw:px-2 tw:sm:px-5 tw:py-[14px] tw:border-l tw:border-b tw:border-[#e8e8e8] tw:flex tw:items-center",
    changed ? "tw:bg-[#fff8e8]" : muted ? "tw:bg-[#f3f3f3]" : "tw:bg-white",
    right && "tw:border-r",
    right && isLast && "tw:rounded-br-[5px]"
  )}>
    {children}
  </div>
);

const Val = ({ text = "-", secondary = "", bold = false }) => (
  <div className="tw:flex tw:flex-col tw:gap-[2px] tw:w-full tw:h-full tw:justify-center">
    <span className={cn("tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px] tw:text-[#282828]", bold && "tw:font-bold")}>{text || "-"}</span>
    {secondary ? <span className="tw:font-hanken-grotesk tw:text-[13px] tw:leading-[18.5px] tw:text-[#7c7b7b]">{secondary}</span> : null}
  </div>
);

const MultiVal = ({ lines = [], bold = false }) => (
  <div className="tw:flex tw:flex-col tw:gap-[2px] tw:w-full tw:justify-center">
    {lines.length ? (
      lines.map((l, i) => (
        <span key={i} className={cn("tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px] tw:text-[#282828]", bold && "tw:font-bold")}>{l}</span>
      ))
    ) : (
      <span className="tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px] tw:text-[#8D8D8D]">-</span>
    )}
  </div>
);

const Row = ({ label, before, after, changed = false, beforeMuted = false, afterMuted = false, isLast = false }) => (
  <div className="tw:flex tw:w-full">
    <LabelCell label={label} isFirst={isLast} />
    <ValueCell muted={beforeMuted}>{before}</ValueCell>
    <ValueCell right isLast={isLast} changed={changed} muted={afterMuted}>{after}</ValueCell>
  </div>
);

// ─── Main component ────────────────────────────────────────────────────────────

const ComparisonTable = ({ selectedRequest, booking, statusValue }) => {
  const useCurrentAsAfter = statusValue === "APPROVED" || statusValue === "REJECTED";

  const oldBooking = selectedRequest?.previousBooking || selectedRequest?.previousData || {};
  const requestedBooking = selectedRequest?.editBooking || selectedRequest || {};
  const currentBooking = booking || {};
  const calculatedEditBooking = useCurrentAsAfter ? currentBooking : requestedBooking;
  const changes = selectedRequest?.changesIdentification || {};

  const getChangeData = (key) => {
    const item = changes?.[key] || {};
    const oldRaw = oldBooking?.[key];
    const newRaw = calculatedEditBooking?.[key];
    const oldValue = item?.old !== undefined ? item.old : oldRaw;
    const newValue = item?.new !== undefined ? item.new : newRaw;
    const computedChanged = isChangedByKey(key, oldValue, newValue);
    return {
      oldValue,
      newValue,
      changed: useCurrentAsAfter ? computedChanged : (typeof item?.changed === "boolean" ? item.changed : computedChanged),
    };
  };

  const bookingTypeChange = getChangeData("bookingType");
  const paxChange = getChangeData("pax");
  const luggageChange = getChangeData("luggage");
  const pickupLocationChange = getChangeData("pickupLocation");
  const destinationsChange = getChangeData("destinations");
  const durationChange = getChangeData("duration");
  const billedDurationChange = getChangeData("billedDuration");
  const travelAreaChange = getChangeData("travelArea");
  const pickupDateTimeChange = getChangeData("pickupDateTime");
  const dropoffDateTimeChange = getChangeData("customerDropoffDateTime");
  const pickupFlightChange = getChangeData("pickupFlightNo");
  const pickupTerminalChange = getChangeData("pickupTerminal");
  const dropoffFlightChange = getChangeData("dropoffFlightNo");
  const dropoffTerminalChange = getChangeData("dropoffTerminal");
  const meetAssistChange = getChangeData("meetAssist");
  const bookingInfoChange = getChangeData("bookingInfo");
  const specialRequestChange = getChangeData("message");
  const passengerNameChange = getChangeData("passengerName");
  const passengerTitleChange = getChangeData("passengerPronounced");
  const passengerPrefixChange = getChangeData("passengerPrefixNumber");
  const passengerPhoneChange = getChangeData("passengerPhoneNumber");
  const pickupSignHonoChange = getChangeData("pickupSignHonorific");
  const pickupSignChange = getChangeData("pickupSign");
  const totalPriceChange = getChangeData("totalPrice");
  const meetAssistPriceChange = getChangeData("totalMeetAssistPrice");

  const beforeVehicles = Array.isArray(bookingInfoChange?.oldValue) ? bookingInfoChange.oldValue : [];
  const afterVehicles = Array.isArray(bookingInfoChange?.newValue) ? bookingInfoChange.newValue : [];

  const vehiclePairs = (() => {
    const pairs = [];
    const usedAfter = new Set();
    for (let i = 0; i < beforeVehicles.length; i++) {
      const vB = beforeVehicles[i];
      const bName = vB?.vehicleTypeName || vB?.vehicleTypeID?.typename || "";
      const matchIdx = afterVehicles.findIndex((vA, j) => {
        if (usedAfter.has(j)) return false;
        return bName && bName === (vA?.vehicleTypeName || vA?.vehicleTypeID?.typename || "");
      });
      if (matchIdx !== -1) { usedAfter.add(matchIdx); pairs.push({ before: vB, after: afterVehicles[matchIdx] }); }
      else pairs.push({ before: vB, after: null });
    }
    const leftover = afterVehicles.filter((_, j) => !usedAfter.has(j));
    let li = 0;
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i].after === null && li < leftover.length) pairs[i].after = leftover[li++];
    }
    while (li < leftover.length) pairs.push({ before: null, after: leftover[li++] });
    return pairs;
  })();

  const isByTheHour = bookingTypeChange?.oldValue === "byTheHour" || bookingTypeChange?.newValue === "byTheHour";
  const showTransfer =
    oldBooking?.isAirportTransfer || calculatedEditBooking?.isAirportTransfer ||
    oldBooking?.isStationTransfer || calculatedEditBooking?.isStationTransfer;
  const fromAirportOrStation =
    oldBooking?.isFromAirport || calculatedEditBooking?.isFromAirport ||
    oldBooking?.isFromStation || calculatedEditBooking?.isFromStation;
  const toAirportOrStation =
    oldBooking?.isToAirport || calculatedEditBooking?.isToAirport ||
    oldBooking?.isToStation || calculatedEditBooking?.isToStation;

  const oldPassengerFull = `${passengerTitleChange?.oldValue || oldBooking?.passengerPronounced || ""} ${(passengerNameChange?.oldValue ?? oldBooking?.passengerName) || ""}`.trim();
  const newPassengerFull = `${passengerTitleChange?.newValue || calculatedEditBooking?.passengerPronounced || ""} ${(passengerNameChange?.newValue ?? calculatedEditBooking?.passengerName) || ""}`.trim();
  const oldPhone = `(${passengerPrefixChange?.oldValue || oldBooking?.passengerPrefixNumber || "-"}) ${(passengerPhoneChange?.oldValue ?? oldBooking?.passengerPhoneNumber) || ""}`;
  const newPhone = `(${passengerPrefixChange?.newValue || calculatedEditBooking?.passengerPrefixNumber || "-"}) ${(passengerPhoneChange?.newValue ?? calculatedEditBooking?.passengerPhoneNumber) || ""}`;

  const diffPrice = Number(totalPriceChange?.newValue || 0) - Number(totalPriceChange?.oldValue || 0);
  const userDeferredPaymentEligible = Boolean(booking?.customer?.user?.deferredPaymentEligible || booking?.user?.deferredPaymentEligible);
  const oldBookingPaymentInfo = getOldBookingPaymentInfo(oldBooking, userDeferredPaymentEligible);

  const formatVehiclePrices = (v) => {
    if (!v) return [];
    const rows = [
      { label: "Charter Cost", value: formatJPY(v.total) },
      { label: "Late Night Fee", value: formatJPY(v.nightPrice) },
    ];
    if (Number(v?.discount?.rate || 0) > 0) {
      rows.push({ label: `Discount (${v.discount?.rate}%)`, value: `-${formatJPY(v.discount?.amount)}` });
    }
    rows.push(
      { label: "Toll / Travel Area Fee", value: formatJPY((v.tollFare || 0) + (v.travelAreaPrice || 0)) },
      { label: "Long Distance Surcharge", value: formatJPY(v.longDistanceSurcharge) },
      { label: "Add-on (Child Seat)", value: formatJPY(v.childSeatPrice) },
    );
    return rows;
  };

  const getChildSeatLines = (v) => {
    if (!v || !Array.isArray(v?.childSeat)) return [];
    const seats = v.childSeat.filter(s => Number(s?.childSeatCount || 0) > 0).map(s => `${s?.childSeatType} x${s?.childSeatCount}`);
    return seats.length ? seats : ["None"];
  };

  const TERMINAL_LABEL_MAP = {
    "terminal-1": "Terminal 1",
    "terminal-2": "Terminal 2",
    "terminal-3": "Terminal 3",
    "terminal-1-domestic": "Terminal 1 (Domestic)",
    "terminal-2-domestic": "Terminal 2 (Domestic)",
    "terminal-2-international": "Terminal 2 (International)",
    "terminal-3-international": "Terminal 3 (International)",
  };

  const formatTerminalLabel = (terminalValue) => {
    if (!terminalValue) return "-";
    const normalized = String(terminalValue).trim().toLowerCase();
    return TERMINAL_LABEL_MAP[normalized] || terminalValue;
  };

  return (
    <section className="tw:w-full tw:min-w-[640px] tw:overflow-hidden">

      {/* Column header */}
      <div className="tw:flex tw:w-full">
        <div className="tw:w-[90px] tw:sm:w-[225px] tw:h-[68px] tw:bg-white tw:border-l tw:border-t tw:border-[#e8e8e8] tw:rounded-tl-[5px]" />
        <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center">
          <span className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold tw:font-hanken-grotesk">Before</span>
        </div>
        <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-l tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center tw:rounded-tr-[5px]">
          <span className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold tw:font-hanken-grotesk">After</span>
        </div>
      </div>

      {/* ── General Information ── */}
      <SectionRow title="General Information" />

      <Row
        label="Transfer Type"
        before={<Val text={formatTransferType(bookingTypeChange?.oldValue, oldBooking?.isAirportTransfer)} />}
        after={<Val text={formatTransferType(bookingTypeChange?.newValue, calculatedEditBooking?.isAirportTransfer)} bold={bookingTypeChange?.changed} />}
        changed={bookingTypeChange?.changed}
      />

      <Row
        label="Pax & Luggage"
        before={<MultiVal lines={[formatPaxGroup(paxChange?.oldValue), formatLuggageGroup(luggageChange?.oldValue)]} />}
        after={<MultiVal lines={[formatPaxGroup(paxChange?.newValue), formatLuggageGroup(luggageChange?.newValue)]} bold={paxChange?.changed || luggageChange?.changed} />}
        changed={paxChange?.changed || luggageChange?.changed}
      />

      <Row
        label="Pick-up Location"
        before={<Val text={getLocationDetail(pickupLocationChange?.oldValue).name} secondary={getLocationDetail(pickupLocationChange?.oldValue).address} />}
        after={<Val text={getLocationDetail(pickupLocationChange?.newValue).name} secondary={getLocationDetail(pickupLocationChange?.newValue).address} bold={pickupLocationChange?.changed} />}
        changed={pickupLocationChange?.changed}
      />

      {/* Destinations (multi-row) */}
      <div className="tw:flex tw:w-full">
        <LabelCell label="Drop-off Location" />
        <div className="tw:flex tw:flex-1 tw:flex-col">
          {(() => {
            const oldDests = Array.isArray(destinationsChange?.oldValue) ? destinationsChange.oldValue : [];
            const newDests = Array.isArray(destinationsChange?.newValue) ? destinationsChange.newValue : [];
            const rowCount = Math.max(oldDests.length, newDests.length, 1);
            return Array.from({ length: rowCount }).map((_, idx) => {
              const oldLoc = oldDests[idx] ? getLocationDetail(oldDests[idx]) : null;
              const newLoc = newDests[idx] ? getLocationDetail(newDests[idx]) : null;
              return (
                <div key={idx} className="tw:flex tw:w-full">
                  <ValueCell muted={!oldLoc}>
                    {oldLoc ? <Val text={oldLoc.name} secondary={oldLoc.address} /> : <Val text="-" />}
                  </ValueCell>
                  <ValueCell changed={destinationsChange?.changed && !!newLoc} muted={!newLoc}>
                    {newLoc ? <Val text={newLoc.name} secondary={newLoc.address} bold={destinationsChange?.changed} /> : <Val text="-" />}
                  </ValueCell>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {isByTheHour && (
        <>
          <Row
            label="Usage Duration"
            before={<Val text={formatDurationText(durationChange?.oldValue)} secondary={bookingTypeChange?.oldValue === "byTheHour" ? `Billed: ${formatBilledHours(billedDurationChange?.oldValue)}` : undefined} />}
            after={<Val text={formatDurationText(durationChange?.newValue)} secondary={bookingTypeChange?.newValue === "byTheHour" ? `Billed: ${formatBilledHours(billedDurationChange?.newValue)}` : undefined} bold={durationChange?.changed} />}
            changed={durationChange?.changed}
          />
          <Row
            label="Travel Area"
            before={<Val text={formatTravelArea(travelAreaChange?.oldValue)} />}
            after={<Val text={formatTravelArea(travelAreaChange?.newValue)} bold={travelAreaChange?.changed} />}
            changed={travelAreaChange?.changed}
          />
        </>
      )}

      <Row
        label="Pick-up Time"
        before={<Val text={formatDateTime(pickupDateTimeChange?.oldValue)} />}
        after={<Val text={formatDateTime(pickupDateTimeChange?.newValue)} bold={pickupDateTimeChange?.changed} />}
        changed={pickupDateTimeChange?.changed}
      />

      <Row
        label="Est. Drop-off Time"
        before={<Val text={formatDateTime(dropoffDateTimeChange?.oldValue)} />}
        after={<Val text={formatDateTime(dropoffDateTimeChange?.newValue)} bold={dropoffDateTimeChange?.changed} />}
        changed={dropoffDateTimeChange?.changed}
      />

      {/* ── Vehicle Selection ── */}
      <SectionRow title="Vehicle Selection" />

      {vehiclePairs.map(({ before: vB, after: vA }, idx) => {
        const vehicleChanged = Boolean(vB || vA) && !deepEqual(normalizeVehicleId(vB), normalizeVehicleId(vA));
        return (
          <Row
            key={`vehicle-${idx}`}
            label={vehiclePairs.length > 1 ? `Vehicle #${idx + 1}` : "Vehicle"}
            before={vB ? <Val text={vB?.vehicleTypeName || vB?.vehicleTypeID?.typename || "-"} /> : <Val text="-" />}
            after={vA ? <Val text={vA?.vehicleTypeName || vA?.vehicleTypeID?.typename || "-"} bold={vehicleChanged} /> : <Val text="-" />}
            changed={vehicleChanged}
            beforeMuted={!vB}
            afterMuted={!vA}
          />
        );
      })}

      {/* ── Passenger Information ── */}
      <SectionRow title="Passenger Information" />

      <Row
        label="Passenger Name"
        before={<Val text={oldPassengerFull || "-"} />}
        after={<Val text={newPassengerFull || "-"} bold={passengerNameChange?.changed || passengerTitleChange?.changed} />}
        changed={passengerNameChange?.changed || passengerTitleChange?.changed}
      />

      <Row
        label="Phone Number"
        before={<Val text={oldPhone} />}
        after={<Val text={newPhone} bold={passengerPhoneChange?.changed || passengerPrefixChange?.changed} />}
        changed={passengerPhoneChange?.changed || passengerPrefixChange?.changed}
      />

      {/* ── Additional Information ── */}
      <SectionRow title="Additional Information & Services" />

      {showTransfer && (
        <>
          {fromAirportOrStation && (
            <>
              <Row
                label="Pick-up Flight No."
                before={<Val text={pickupFlightChange?.oldValue || "-"} />}
                after={<Val text={pickupFlightChange?.newValue || "-"} bold={pickupFlightChange?.changed} />}
                changed={pickupFlightChange?.changed}
              />
              {(oldBooking?.isFromAirport || calculatedEditBooking?.isFromAirport) && (
                <>
                  <Row
                    label="Arrival Terminal"
                    before={<Val text={formatTerminalLabel(pickupTerminalChange?.oldValue) || "-"} />}
                    after={<Val text={formatTerminalLabel(pickupTerminalChange?.newValue) || "-"} bold={pickupTerminalChange?.changed} />}
                    changed={pickupTerminalChange?.changed}
                  />
                  <Row
                    label="Pick-up Sign"
                    before={<Val text={getPickupSign(pickupSignHonoChange?.oldValue, pickupSignChange?.oldValue)} />}
                    after={<Val text={getPickupSign(pickupSignHonoChange?.newValue, pickupSignChange?.newValue)} bold={pickupSignChange?.changed} />}
                    changed={pickupSignChange?.changed}
                  />
                </>
              )}
            </>
          )}
          {toAirportOrStation && (
            <>
              <Row
                label="Drop-off Flight No."
                before={<Val text={dropoffFlightChange?.oldValue || "-"} />}
                after={<Val text={dropoffFlightChange?.newValue || "-"} bold={dropoffFlightChange?.changed} />}
                changed={dropoffFlightChange?.changed}
              />
              {(oldBooking?.isToAirport || calculatedEditBooking?.isToAirport) && (
                <Row
                  label="Departure Terminal"
                  before={<Val text={formatTerminalLabel(dropoffTerminalChange?.oldValue) || "-"} />}
                  after={<Val text={formatTerminalLabel(dropoffTerminalChange?.newValue) || "-"} bold={dropoffTerminalChange?.changed} />}
                  changed={dropoffTerminalChange?.changed}
                />
              )}
            </>
          )}
          <Row
            label="Meet & Assist"
            before={<Val text={formatMeetAssist(meetAssistChange?.oldValue).title} />}
            after={<Val text={formatMeetAssist(meetAssistChange?.newValue).title} bold={meetAssistChange?.changed} />}
            changed={meetAssistChange?.changed}
          />
        </>
      )}

      {/* Add-on (child seats) */}
      <div className="tw:flex tw:w-full">
        <LabelCell label="Add-on Services" />
        <div className="tw:flex tw:flex-1 tw:flex-col">
          {vehiclePairs.map(({ before: vB, after: vA }, idx) => {
            const addonChanged = !deepEqual(
              (vB?.childSeat || []).filter(s => s.childSeatCount > 0),
              (vA?.childSeat || []).filter(s => s.childSeatCount > 0)
            );
            const label = vehiclePairs.length > 1 ? (vB || vA)?.vehicleTypeName || `Vehicle #${idx + 1}` : null;
            return (
              <div key={idx} className="tw:flex tw:w-full">
                <ValueCell muted={!vB}>
                  {label && <span className="tw:text-[12px] tw:text-[#8D8D8D] tw:block tw:mb-1">{label}</span>}
                  <MultiVal lines={vB ? getChildSeatLines(vB) : ["-"]} />
                </ValueCell>
                <ValueCell changed={addonChanged && !!vA} muted={!vA}>
                  {label && <span className="tw:text-[12px] tw:text-[#8D8D8D] tw:block tw:mb-1">{label}</span>}
                  <MultiVal lines={vA ? getChildSeatLines(vA) : ["-"]} bold={addonChanged && !!vA} />
                </ValueCell>
              </div>
            );
          })}
        </div>
      </div>

      <Row
        label="Special Request"
        before={<Val text={formatMessage(specialRequestChange?.oldValue)} />}
        after={<Val text={formatMessage(specialRequestChange?.newValue)} bold={specialRequestChange?.changed} />}
        changed={specialRequestChange?.changed}
      />

      {/* ── Pricing ── */}
      <SectionRow title="Pricing" />

      <div className="tw:flex tw:w-full">
        <LabelCell label="Price Detail" />
        <div className="tw:flex tw:flex-1 tw:flex-col">
          {vehiclePairs.map(({ before: vB, after: vA }, idx) => {
            const priceChanged = !deepEqual(
              { total: vB?.total, tollFare: vB?.tollFare, nightPrice: vB?.nightPrice },
              { total: vA?.total, tollFare: vA?.tollFare, nightPrice: vA?.nightPrice }
            );
            const label = vehiclePairs.length > 1 ? (vB || vA)?.vehicleTypeName || `Vehicle #${idx + 1}` : null;
            return (
              <div key={idx} className="tw:flex tw:w-full">
                <ValueCell muted={!vB}>
                  {label && <span className="tw:text-[12px] tw:text-[#8D8D8D] tw:block tw:mb-2">{label}</span>}
                  {vB ? (
                    <div className="tw:flex tw:w-full tw:flex-col tw:gap-1">
                      {formatVehiclePrices(vB).map((r, i) => (
                        <div key={i} className="tw:flex tw:w-full tw:justify-between tw:gap-2">
                          <span className="tw:text-[13px] tw:text-[#8D8D8D]">{r.label}</span>
                          <span className="tw:text-[15px] tw:text-[#282828]">{r.value}</span>
                        </div>
                      ))}
                    </div>
                  ) : <Val text="-" />}
                </ValueCell>
                <ValueCell changed={priceChanged} muted={!vA}>
                  {label && <span className="tw:text-[12px] tw:text-[#8D8D8D] tw:block tw:mb-2">{label}</span>}
                  {vA ? (
                    <div className="tw:flex tw:w-full tw:flex-col tw:gap-1">
                      {formatVehiclePrices(vA).map((r, i) => (
                        <div key={i} className="tw:flex tw:w-full tw:justify-between tw:gap-2">
                          <span className="tw:text-[13px] tw:text-[#8D8D8D]">{r.label}</span>
                          <span className={cn("tw:text-[15px] tw:text-[#282828]", priceChanged && "tw:font-[700]")}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                  ) : <Val text="-" />}
                </ValueCell>
              </div>
            );
          })}

          {/* Meet assist */}
          <div className="tw:flex tw:w-full">
            <ValueCell>
              <div className="tw:flex tw:w-full tw:justify-between tw:gap-2">
                <span className="tw:text-[13px] tw:text-[#8D8D8D]">Meet & Assist Service</span>
                <span className="tw:text-[15px] tw:text-[#282828]">{formatJPY(meetAssistPriceChange?.oldValue)}</span>
              </div>
            </ValueCell>
            <ValueCell changed={meetAssistPriceChange?.changed}>
              <div className="tw:flex tw:w-full tw:justify-between tw:gap-2">
                <span className="tw:text-[13px] tw:text-[#8D8D8D]">Meet & Assist Service</span>
                <span className={cn("tw:text-[15px] tw:text-[#282828]", meetAssistPriceChange?.changed && "tw:font-[700]")}>{formatJPY(meetAssistPriceChange?.newValue)}</span>
              </div>
            </ValueCell>
          </div>
        </div>
      </div>

      {/* Total price — last row */}
      <div className="tw:flex tw:w-full">
        <LabelCell label="Total Price" isFirst />
        <ValueCell>
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <span className="tw:font-hanken-grotesk tw:text-[13px] tw:italic tw:text-[#8D8D8D]">{oldBookingPaymentInfo}</span>
            <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[500] tw:text-[#282828]">{formatJPY(totalPriceChange?.oldValue)}</span>
          </div>
        </ValueCell>
        <ValueCell right isLast changed={totalPriceChange?.changed}>
          <div className="tw:flex tw:flex-col tw:items-end tw:gap-[3px] tw:w-full">
            <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[700] tw:text-[#282828]">{formatJPY(totalPriceChange?.newValue)}</span>
            {totalPriceChange?.changed && (
              <span className="tw:font-hanken-grotesk tw:text-[13px] tw:font-[700] tw:text-[#EA4335]">
                {diffPrice > 0 ? "+" : ""}{formatJPY(diffPrice)}
              </span>
            )}
          </div>
        </ValueCell>
      </div>

    </section>
  );
};

export default ComparisonTable;
